import { Edit, ExpandMore, Save, StickyNote2 } from "@mui/icons-material";
import {
  alpha,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import MDEditor from "@uiw/react-md-editor";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useState } from "react";
import { toast } from "sonner";
import {
  useGetCustomerNoteQuery,
  useUpdateCustomerNoteMutation,
} from "../../../store/services/customerService";

interface CustomerNotesCardProps {
  customerNo: number;
}

const CustomerNotesCard = ({ customerNo }: CustomerNotesCardProps) => {
  const { data: note, isLoading, error } = useGetCustomerNoteQuery(customerNo);
  const [updateNote, { isLoading: isUpdating }] =
    useUpdateCustomerNoteMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  // Configure MDEditor with essential toolbar options
  const mdEditorHeight = 400; // Larger height for modal
  const previewMaxLength = 150; // Character limit for preview

  const handleEditStart = () => {
    if (note) {
      setEditValue(note.Data);
    }
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditValue("");
  };

  const handleEditSave = async () => {
    try {
      await updateNote({
        customerId: customerNo,
        data: editValue,
      }).unwrap();

      setIsEditing(false);
      toast.success("Anteckning uppdaterad");
    } catch {
      toast.error("Kunde inte uppdatera anteckningen");
    }
  };

  // Helper function to get truncated text
  const getTruncatedText = (text: string) => {
    if (text.length <= previewMaxLength) return text;
    return text.substring(0, previewMaxLength) + "...";
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            Inga anteckningar hittades för denna kund
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "d MMM", { locale: sv });
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={(theme) => ({
                  width: 22,
                  height: 22,
                  borderRadius: "6px",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  display: "grid",
                  placeItems: "center",
                  "& .MuiSvgIcon-root": { fontSize: 14 },
                })}
              >
                <StickyNote2 />
              </Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  color: "#181614",
                }}
              >
                Kundanteckning
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleEditStart}
              disabled={isUpdating}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ minHeight: 120 }}>
            {note ? (
              <Box>
                <Box
                  sx={{
                    maxHeight:
                      note.Data.length > previewMaxLength ? 80 : "none",
                    overflow: "hidden",
                    mb: 1,
                  }}
                >
                  <MDEditor.Markdown
                    source={getTruncatedText(note.Data)}
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.5",
                      color: "#333",
                      backgroundColor: "transparent",
                      padding: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  />
                </Box>

                {note.Data.length > previewMaxLength && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setIsExpanded(true)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Läs mer
                    <ExpandMore fontSize="small" />
                  </Link>
                )}

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  {note.Modified !== note.Created
                    ? `Uppdaterad ${formatDate(note.Modified)} av ${
                        note.ChangedBy.Name
                      }`
                    : `Skapad ${formatDate(note.Created)} av ${
                        note.CreatedBy.Name
                      }`}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
              >
                Ingen anteckning ännu. Klicka på redigera för att lägga till.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Full view modal */}
      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Kundanteckning</Typography>
        </DialogTitle>
        <DialogContent>
          {note && (
            <Box>
              <MDEditor.Markdown
                source={note.Data}
                style={{
                  fontSize: "14px",
                  lineHeight: "1.6",
                  color: "#333",
                  backgroundColor: "transparent",
                  padding: 0,
                  whiteSpace: "pre-wrap",
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: "block" }}
              >
                {note.Modified !== note.Created
                  ? `Uppdaterad ${formatDate(note.Modified)} av ${
                      note.ChangedBy.Name
                    }`
                  : `Skapad ${formatDate(note.Created)} av ${
                      note.CreatedBy.Name
                    }`}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsExpanded(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>

      {/* Edit modal */}
      <Dialog
        open={isEditing}
        onClose={handleEditCancel}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Redigera kundanteckning</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <MDEditor
              value={editValue}
              onChange={(value) => setEditValue(value || "")}
              height={mdEditorHeight}
              data-color-mode="light"
              hideToolbar={isUpdating}
              preview="edit"
              textareaProps={{
                placeholder: "Skriv anteckning...",
                disabled: isUpdating,
                style: {
                  fontSize: "14px",
                  lineHeight: "1.6",
                  whiteSpace: "pre-wrap",
                },
                onKeyDown: (e) => {
                  // Ensure Enter creates new lines
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.stopPropagation();
                  }
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleEditCancel}
            disabled={isUpdating}
            color="secondary"
          >
            Avbryt
          </Button>
          <Button
            onClick={handleEditSave}
            disabled={isUpdating}
            color="primary"
            variant="contained"
            startIcon={<Save />}
          >
            {isUpdating ? "Sparar..." : "Spara"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerNotesCard;
